import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CreateConcessionForm.css";
import axios from "axios";

const CreateConcessionForm = ({ onClose, concession = null }) => {
  const isEditMode = !!concession;
  const formTitle = isEditMode ? "Modifier la concession" : "Créer une concession";
  const submitButtonText = isEditMode ? "Mettre à jour" : "Créer";
  
  const [formData, setFormData] = useState({
    nom: concession?.nom || "",
    ville: concession?.ville || "",
    siret: concession?.siret || "",
    code_postal: concession?.code_postal || "",
    numero_voie: concession?.numero_voie || "",
    extension: concession?.extension || "",
    type_voie: concession?.type_voie || "",
    nom_voie: concession?.nom_voie || "",
    telephone: concession?.telephone || "",
    mail: concession?.mail || "",
    espace_note: concession?.espace_note || "",
    groupe_id: concession?.groupe_id || 0,
  });

  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [groupes, setGroupes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

  useEffect(() => {
    document.body.classList.add('modal-open');
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  useEffect(() => {
    if (concession) {
      setFormData({
        nom: concession.nom || "",
        ville: concession.ville || "",
        siret: concession.siret || "",
        code_postal: concession.code_postal || "",
        numero_voie: concession.numero_voie || "",
        extension: concession.extension || "",
        type_voie: concession.type_voie || "",
        nom_voie: concession.nom_voie || "",
        telephone: concession.telephone || "",
        mail: concession.mail || "",
        espace_note: concession.espace_note || "",
        groupe_id: concession.groupe_id || 0,
      });
    }
  }, [concession]);

  useEffect(() => {
    const fetchGroupes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/groups`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Erreur lors de la récupération des groupes");
        }

        const data = await response.json();
        setGroupes(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes:", error);
        setError("Erreur lors de la récupération des groupes");
      }
    };

    fetchGroupes();
  }, []);

  useEffect(() => {
    if (isEditMode && groupes.length > 0 && concession?.groupe_id) {
      setFormData(prev => ({
        ...prev,
        groupe_id: concession.groupe_id
      }));
    }
  }, [groupes, isEditMode, concession]);
  

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.nom || formData.nom.trim() === "") {
      errors.nom = "Le nom de la concession est requis";
      isValid = false;
    }

    if (!formData.ville || formData.ville.trim() === "") {
      errors.ville = "La ville est requise";
      isValid = false;
    }

    if (!formData.siret) {
      errors.siret = "Le SIRET est requis";
      isValid = false;
    } else if (formData.siret.length !== 14) {
      errors.siret = "Le SIRET doit contenir exactement 14 chiffres";
      isValid = false;
    }

    if (!formData.numero_voie || formData.numero_voie.trim() === "") {
      errors.numero_voie = "Le numéro de voie est requis";
      isValid = false;
    }

    if (!formData.type_voie || formData.type_voie.trim() === "") {
      errors.type_voie = "Le type de voie est requis";
      isValid = false;
    }

    if (!formData.nom_voie || formData.nom_voie.trim() === "") {
      errors.nom_voie = "Le nom de la voie est requis";
      isValid = false;
    }

    if (!formData.code_postal || formData.code_postal.trim() === "") {
      errors.code_postal = "Le code postal est requis";
      isValid = false;
    } else if (formData.code_postal.length !== 5) {
      errors.code_postal = "Le code postal doit contenir 5 chiffres";
      isValid = false;
    }

    if (!formData.telephone || formData.telephone.trim() === "") {
      errors.telephone = "Le téléphone est requis";
      isValid = false;
    } else if (formData.telephone.length !== 10) {
      errors.telephone = "Le téléphone doit contenir 10 chiffres";
      isValid = false;
    }

    if (!formData.mail || formData.mail.trim() === "") {
      errors.mail = "L'email est requis";
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.mail)) {
      errors.mail = "Format d'email invalide";
      isValid = false;
    }

    if (!formData.groupe_id) {
      errors.groupe_id = "Le groupe est requis";
      isValid = false;
    }

    setFieldErrors(errors);
    
    if (!isValid) {
      setError("Veuillez corriger les erreurs dans le formulaire");
    }
    
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError(null);

    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: null
      });
    }

    if (name === "siret" && value) {
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Le SIRET ne doit contenir que des chiffres"
        });
        return;
      }
      
      const onlyNumbers = value.replace(/[^\d]/g, "");
      if (onlyNumbers.length <= 14) {
        setFormData((prev) => ({
          ...prev,
          [name]: onlyNumbers,
        }));
        
        if (onlyNumbers.length > 0 && onlyNumbers.length < 14) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le SIRET doit contenir 14 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      return;
    }

    if (name === "telephone" && value) {
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Le téléphone ne doit contenir que des chiffres"
        });
        return;
      }
      
      const onlyNumbers = value.replace(/[^\d]/g, "");
      if (onlyNumbers.length <= 10) {
        setFormData((prev) => ({
          ...prev,
          [name]: onlyNumbers,
        }));
        
        if (onlyNumbers.length > 0 && onlyNumbers.length < 10) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le téléphone doit contenir 10 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      return;
    }

    if (name === "code_postal" && value) {
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Le code postal ne doit contenir que des chiffres"
        });
        return;
      }
      
      const onlyNumbers = value.replace(/[^\d]/g, "");
      if (onlyNumbers.length <= 5) {
        setFormData((prev) => ({
          ...prev,
          [name]: onlyNumbers,
        }));
        
        if (onlyNumbers.length > 0 && onlyNumbers.length < 5) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le code postal doit contenir 5 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      return;
    }

    if (name === 'mail') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailRegex.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Format d'email invalide"
        });
      } else {
        setFieldErrors({
          ...fieldErrors,
          [name]: null
        });
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      let response;
      
      if (isEditMode) {
        response = await axios.put(
          `${API_URL}/concessions/${concession.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data && response.data.success) {
          alert("Concession mise à jour avec succès");
          onClose();
        }
      } else {
        response = await axios.post(
          `${API_URL}/concessions`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data && response.data.success) {
          alert("Concession créée avec succès");
          onClose();
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      
      if (error.response?.data) {
        const { errorType, field, message } = error.response.data;
        
        if (errorType === "DUPLICATE_SIRET" && field === "siret") {
          setFieldErrors({
            ...fieldErrors,
            siret: "Ce SIRET est déjà utilisé par une autre concession"
          });
        } else if (errorType === "DUPLICATE_NAME" && field === "nom") {
          setFieldErrors({
            ...fieldErrors,
            nom: "Ce nom est déjà utilisé par une autre concession"
          });
        } else {
          setError(message || "Une erreur est survenue lors de l'enregistrement");
        }
      } else {
        setError("Une erreur est survenue lors de l'enregistrement");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{formTitle}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          <div className="form-section">
            <div className="form-group">
              <label className="required-field">Nom de la concession</label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                placeholder="Ex: VéhiDoc Rennes"
                autoFocus
                className={fieldErrors.nom ? "input-error" : ""}
              />
              {fieldErrors.nom && <div className="field-error">{fieldErrors.nom}</div>}
            </div>

            <div className="form-group">
              <label className="required-field">Groupe</label>
              <select
                name="groupe_id"
                value={formData.groupe_id}
                onChange={handleChange}
                required
                className={fieldErrors.groupe_id ? "input-error" : ""}
              >
                <option value="">Sélectionner un groupe</option>
                {groupes.map((groupe) => (
                  <option key={groupe.id} value={groupe.id}>
                    {groupe.nom}
                  </option>
                ))}
              </select>
              {fieldErrors.groupe_id && <div className="field-error">{fieldErrors.groupe_id}</div>}
            </div>

            <div className="form-group">
              <label className="required-field">SIRET (14 chiffres)</label>
              <input
                type="text"
                name="siret"
                value={formData.siret}
                onChange={handleChange}
                required
                maxLength={14}
                placeholder="Ex: 12345678901234"
                className={fieldErrors.siret ? "input-error" : ""}
              />
              {fieldErrors.siret && <div className="field-error">{fieldErrors.siret}</div>}
            </div>
          </div>

          <div className="form-section">
            <div className="form-group-row">
              <div className="form-group">
                <label className="required-field">Ville</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Rennes"
                  className={fieldErrors.ville ? "input-error" : ""}
                />
                {fieldErrors.ville && <div className="field-error">{fieldErrors.ville}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Code Postal</label>
                <input
                  type="text"
                  name="code_postal"
                  value={formData.code_postal}
                  onChange={handleChange}
                  placeholder="Ex: 35000"
                  required
                  maxLength={5}
                  className={fieldErrors.code_postal ? "input-error" : ""}
                />
                {fieldErrors.code_postal && <div className="field-error">{fieldErrors.code_postal}</div>}
              </div>
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="required-field">Numéro de Voie</label>
                <input
                  type="text"
                  name="numero_voie"
                  value={formData.numero_voie}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 123"
                  className={fieldErrors.numero_voie ? "input-error" : ""}
                />
                {fieldErrors.numero_voie && <div className="field-error">{fieldErrors.numero_voie}</div>}
              </div>

              <div className="form-group">
                <label>Extension</label>
                <input
                  type="text"
                  name="extension"
                  value={formData.extension}
                  onChange={handleChange}
                  placeholder="Ex: Bis"
                  className={fieldErrors.extension ? "input-error" : ""}
                />
                {fieldErrors.extension && <div className="field-error">{fieldErrors.extension}</div>}
              </div>
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="required-field">Type de Voie</label>
                <input
                  type="text"
                  name="type_voie"
                  value={formData.type_voie}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Rue"
                  className={fieldErrors.type_voie ? "input-error" : ""}
                />
                {fieldErrors.type_voie && <div className="field-error">{fieldErrors.type_voie}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Nom de la Voie</label>
                <input
                  type="text"
                  name="nom_voie"
                  value={formData.nom_voie}
                  onChange={handleChange}
                  required
                  placeholder="Ex: de la République"
                  className={fieldErrors.nom_voie ? "input-error" : ""}
                />
                {fieldErrors.nom_voie && <div className="field-error">{fieldErrors.nom_voie}</div>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label className="required-field">Téléphone</label>
              <input
                type="text"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                required
                placeholder="Ex: 0123456789"
                maxLength={10}
                className={fieldErrors.telephone ? "input-error" : ""}
              />
              {fieldErrors.telephone && <div className="field-error">{fieldErrors.telephone}</div>}
            </div>

            <div className="form-group">
              <label className="required-field">Email</label>
              <input
                type="email"
                name="mail"
                value={formData.mail}
                onChange={handleChange}
                required
                placeholder="Ex: contact@vehidoc.fr"
                className={fieldErrors.mail ? "input-error" : ""}
              />
              {fieldErrors.mail && <div className="field-error">{fieldErrors.mail}</div>}
            </div>

            <div className="form-group">
              <label>Espace Note</label>
              <textarea
                name="espace_note"
                value={formData.espace_note}
                onChange={handleChange}
                placeholder="Notes supplémentaires"
                className={fieldErrors.espace_note ? "input-error" : ""}
              />
              {fieldErrors.espace_note && <div className="field-error">{fieldErrors.espace_note}</div>}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="button secondary" onClick={onClose}>
            Annuler
          </button>
          <button 
            type="button" 
            className="button primary" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Enregistrement..." : submitButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

CreateConcessionForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  concession: PropTypes.shape({
    id: PropTypes.number,
    nom: PropTypes.string,
    ville: PropTypes.string,
    siret: PropTypes.string,
    code_postal: PropTypes.string,
    numero_voie: PropTypes.string,
    extension: PropTypes.string,
    type_voie: PropTypes.string,
    nom_voie: PropTypes.string,
    telephone: PropTypes.string,
    mail: PropTypes.string,
    espace_note: PropTypes.string,
    groupe_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default CreateConcessionForm;
